import { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { Images } from "./Interfaces/ImageData";
import { toast } from "react-toastify";

interface ImageUploaderProps {
  existingImages: Images[]; // Add this prop
  onImagesChange: (images: Images[]) => void;
  maxImages?: number;
}

const ImageUploader = ({
  onImagesChange,
  existingImages = [],
}: ImageUploaderProps) => {
  const [images, setImages] = useState<Images[]>(existingImages);
  const [isUploading, setIsUploading] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Update images when existingImages prop changes
  useEffect(() => {
    setImages(existingImages);
  }, [existingImages]);

  // Upload to ImgBB
  const uploadToImgBB = async (file: File): Promise<Images> => {
    const formData = new FormData();
    formData.append("image", file);
    formData.append("key", "81da00bbc6e7e9135fe4c32296768900");

    const response = await axios.post(
      "https://api.imgbb.com/1/upload",
      formData
    );
    return {
      imageUrl: response.data.data.display_url,
      deleteUrl: response.data.data.delete_url, // Critical for deletion
    };
  };

  // Unable to delete from Img free Api
  // Delete from both state and ImgBB
  const deleteImage = async (index: number) => {
    // const imageToDelete = images[index];

    try {
      // 1. Call ImgBB's actual API endpoint
      // await axios.post(`https://api.imgbb.com/1/delete`, {
      //   key: '49ebfd1d19a5eaae06faec14d7439141', // Same key used for upload
      //   delete_token: extractDeleteToken(imageToDelete.deleteUrl) // Explained below
      // });

      // 2. Update local state
      const updatedImages = images.filter((_, i) => i !== index);
      setImages(updatedImages);
      onImagesChange(updatedImages);
    } catch (error) {
      console.error("Deletion failed:", error);
      toast.error("Could not delete image. Please try again.");
    }
  };

  // Handle drag and drop reordering
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newImages = [...images];
    const draggedItem = newImages[draggedIndex];

    // Remove the dragged item
    newImages.splice(draggedIndex, 1);

    // Insert at the new position
    newImages.splice(dropIndex, 0, draggedItem);

    setImages(newImages);
    onImagesChange(newImages);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Helper to extract token from delete_url
  // const extractDeleteToken = (deleteUrl: string) => {
  //   const url = new URL(deleteUrl);
  //   return url.pathname.split('/').pop(); // Gets the token from URL like "/delete/abc123"
  // };

  // Dropzone setup
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles?.length) {
        setIsUploading(true);
        try {
          const uploadPromises = acceptedFiles.map(uploadToImgBB);
          const newImages = await Promise.all(uploadPromises);
          const updatedImages = [...images, ...newImages];
          setImages(updatedImages);
          onImagesChange(updatedImages);
        } catch (error) {
          console.error("Upload failed:", error);
        } finally {
          setIsUploading(false);
        }
      }
    },
    [images, onImagesChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [".jpeg", ".jpg", ".png"] },
    maxFiles: 9,
    maxSize: 32 * 1024 * 1024,
  });

  return (
    <div className="space-y-4">
      {/* Dropzone Area */}
      {isUploading ? (
        <div className="border-2 border-dashed rounded-lg p-6 text-center border-blue-300 bg-blue-50">
          <div className="flex flex-col items-center justify-center space-y-4">
            {/* Loading Ring */}
            <div className="relative">
              <div className="w-12 h-12 border-4 border-blue-200 rounded-full animate-spin border-t-blue-500"></div>
            </div>
            <p className="text-[var(--secondary)] font-medium">
              Uploading images...
            </p>
            <p className="text-sm text-[var(--secondary-light)]">
              Please wait while your images are being processed
            </p>
          </div>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer ${
            isDragActive ? "border-blue-500 bg-blue-50" : "border-gray-300"
          }`}
        >
          <input {...getInputProps()} />
          <p>
            {isDragActive
              ? "Drop images here"
              : "Drag & drop images, or click to select (max 9)"}
          </p>
        </div>
      )}

      {/* Thumbnail Grid with Delete and Drag/Drop */}
      <div className="grid grid-cols-3 gap-4 mt-4">
        {images.map((img, index) => (
          <div
            key={index}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            className={`relative aspect-square group cursor-move transition-all duration-200 ${
              draggedIndex === index ? "opacity-50 scale-95" : ""
            } ${
              dragOverIndex === index && draggedIndex !== index
                ? "ring-2 ring-blue-500 scale-105"
                : ""
            }`}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <img
              src={img.imageUrl}
              alt={`Preview ${index + 1}`}
              className="w-full h-full object-cover rounded-lg"
            />

            {/* Main Display Indicator */}
            {index === 0 && (
              <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-semibold shadow-lg">
                Main Display
              </div>
            )}

            {/* Drag Handle */}
            <div className="absolute top-2 right-8 bg-black bg-opacity-50 text-white rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                />
              </svg>
            </div>

            {/* Delete Button (shown on hover) */}
            {hoveredIndex === index && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteImage(index);
                }}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 
                           hover:bg-red-600 transition-colors z-10"
                aria-label="Delete image"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Instructions */}
      {images.length > 1 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
          <div className="flex items-center">
            <svg
              className="h-5 w-5 text-gray-400 mr-2"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-sm text-gray-600">
              <strong>Tip:</strong> Drag and drop images to reorder them. The
              first image will be the main display image for your workspace.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
